import type { ReactNode } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type {
  AuditStatFilterPreset,
  SessionClientFilters,
} from "../../utils/bookingFormAuditSession";
import {
  applyAbandonedAtStepFilter,
  applyAuditStatFilterPreset,
  applyBrowserFilter,
  applyCountryFilter,
  applyDeviceFilter,
  applyReachedStepFilter,
  applyTrafficSourceFilter,
  applyLandingPageFilter,
  trafficSourceLabel,
} from "../../utils/bookingFormAuditSession";
import { formatUkDate } from "../../utils/dateUtils";

export interface BookingFormAuditStats {
  totalSessions: number;
  completedSessions: number;
  abandonedSessions: number;
  completionRate: number;
  totalEvents: number;
  avgEventsPerSession: number;
  cookieConsentSessions: number;
  cookieConsentRate: number;
  avgSessionDurationMinutes: number | null;
  paymentFailures: number;
  bookingFailures: number;
  validationFailures: number;
  fareQuoteFailures: number;
  callClicks: number;
  callClickSessions: number;
  stepDropOff: { stepName: string; label: string; count: number }[];
  stepFunnel: { stepName: string; label: string; count: number }[];
  sessionsByDay: { date: string; sessions: number; completed: number }[];
  deviceBreakdown: { deviceType: string; count: number }[];
  browserBreakdown: { browser: string; count: number }[];
  topCountries: { country: string; count: number }[];
  trafficSources: { source: string; label: string; count: number }[];
  topUtmTerms: { term: string; count: number }[];
  topLandingPages: { path: string; count: number }[];
  avgEngagementSeconds: number | null;
  avgScrollDepth: number | null;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatPercent(part: number, whole: number): string {
  if (whole <= 0) return "0%";
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  sub,
  accent = "neutral",
  onClick,
  clickable = false,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: "brand" | "success" | "warning" | "error" | "neutral";
  onClick?: () => void;
  clickable?: boolean;
}) {
  const accentClass = {
    brand: "text-brand-600 dark:text-brand-400",
    success: "text-success-600 dark:text-success-400",
    warning: "text-warning-600 dark:text-warning-500",
    error: "text-error-600 dark:text-error-400",
    neutral: "text-gray-800 dark:text-white/90",
  }[accent];

  const className = `rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6 text-left w-full ${
    clickable
      ? "cursor-pointer transition hover:border-brand-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:hover:border-brand-500/50"
      : ""
  }`;

  const content = (
    <>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${accentClass}`}>
        {typeof value === "number" ? formatNumber(value) : value}
      </p>
      {sub && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{sub}</p>
      )}
      {clickable && (
        <p className="mt-2 text-xs font-medium text-brand-600 dark:text-brand-400">
          View in audit ↓
        </p>
      )}
    </>
  );

  if (clickable && onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

function ChartPanel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
          {title}
        </h3>
        {subtitle ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function ExactDataTable({
  caption,
  columns,
  rows,
  onRowClick,
}: {
  caption: string;
  columns: string[];
  rows: (string | number)[][];
  onRowClick?: (rowIndex: number) => void;
}) {
  if (rows.length === 0) return null;

  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
      <table className="min-w-full text-left text-xs">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
          <tr>
            {columns.map((column) => (
              <th key={column} className="px-3 py-2 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row, index) => (
            <tr
              key={`${caption}-${index}`}
              onClick={onRowClick ? () => onRowClick(index) : undefined}
              className={`text-gray-700 dark:text-gray-300 ${
                onRowClick
                  ? "cursor-pointer hover:bg-brand-50/60 dark:hover:bg-brand-500/10"
                  : ""
              }`}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={`${caption}-${index}-${cellIndex}`}
                  className={`px-3 py-2 tabular-nums ${
                    cellIndex === 0 ? "font-medium text-gray-800 dark:text-white/90" : ""
                  }`}
                >
                  {typeof cell === "number" ? formatNumber(cell) : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BreakdownList<T extends { count: number }>({
  items,
  labelKey,
  total,
  emptyLabel = "No data yet",
  onItemClick,
}: {
  items: T[];
  labelKey: keyof T & string;
  total?: number;
  emptyLabel?: string;
  onItemClick?: (item: T) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }

  const max = Math.max(...items.map((i) => i.count));
  const base = total && total > 0 ? total : items.reduce((sum, i) => sum + i.count, 0);

  return (
    <ul className="space-y-3.5">
      {items.map((item) => {
        const label = String(item[labelKey]);
        const count = item.count;
        const clickable = Boolean(onItemClick) && count > 0;
        return (
          <li key={label}>
            <button
              type="button"
              disabled={!clickable}
              onClick={clickable ? () => onItemClick?.(item) : undefined}
              className={`block w-full text-left ${
                clickable
                  ? "cursor-pointer rounded-lg p-1 -m-1 transition hover:bg-brand-50/60 dark:hover:bg-brand-500/10"
                  : ""
              }`}
            >
              <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
                <span className="capitalize text-gray-700 dark:text-gray-300">
                  {label}
                </span>
                <span className="shrink-0 text-right">
                  <span className="font-semibold tabular-nums text-gray-900 dark:text-white/90">
                    {formatNumber(count)}
                  </span>
                  <span className="ml-2 text-xs tabular-nums text-gray-500">
                    {formatPercent(count, base)}
                  </span>
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400"
                  style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }}
                />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function StatSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-800" />
      <div className="mt-3 h-8 w-16 rounded bg-gray-200 dark:bg-gray-800" />
    </div>
  );
}

const chartBase: ApexOptions = {
  chart: {
    fontFamily: "Outfit, sans-serif",
    toolbar: { show: false },
    animations: {
      enabled: true,
      speed: 450,
    },
  },
  grid: {
    borderColor: "#e5e7eb",
    strokeDashArray: 3,
    padding: { left: 8, right: 12 },
  },
};

interface Props {
  stats: BookingFormAuditStats | null;
  loading: boolean;
  onApplyFilters?: (filters: SessionClientFilters) => void;
}

export default function BookingFormAuditAnalytics({
  stats,
  loading,
  onApplyFilters,
}: Props) {
  const applyStat = (preset: AuditStatFilterPreset) => {
    if (!onApplyFilters) return;
    onApplyFilters(applyAuditStatFilterPreset(preset));
  };

  const applyCustomFilters = (filters: SessionClientFilters) => {
    onApplyFilters?.(filters);
  };
  if (loading) {
    return (
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <StatSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const funnelTotal = Math.max(...stats.stepFunnel.map((s) => s.count), 0);
  const dropOffTotal = stats.stepDropOff.reduce((sum, s) => sum + s.count, 0);
  const consentedSessions = stats.cookieConsentSessions;
  const deviceTotal = stats.deviceBreakdown.reduce((sum, d) => sum + d.count, 0);
  const countryTotal = stats.topCountries.reduce((sum, c) => sum + c.count, 0);
  const browserTotal = stats.browserBreakdown.reduce((sum, b) => sum + b.count, 0);
  const trafficTotal = stats.trafficSources.reduce((sum, t) => sum + t.count, 0);
  const landingTotal = stats.topLandingPages.reduce((sum, p) => sum + p.count, 0);
  const utmTermTotal = stats.topUtmTerms.reduce((sum, t) => sum + t.count, 0);

  const formatEngagementDuration = (seconds: number | null): string => {
    if (seconds == null || seconds <= 0) return "—";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const funnelOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: "end",
        columnWidth: "48%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatNumber(Number(val)),
      offsetY: -18,
      style: {
        fontSize: "11px",
        fontWeight: 600,
        // With background.enabled, style.colors paints the pill (not the text).
        colors: ["#101828"],
      },
      background: {
        enabled: true,
        foreColor: "#ffffff",
        borderRadius: 4,
        padding: 4,
        opacity: 0.95,
        borderWidth: 0,
        dropShadow: { enabled: false },
      },
    },
    colors: ["#465fff"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.25,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: stats.stepFunnel.map((s) => s.label),
      labels: { style: { fontSize: "12px", colors: "#667085" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", colors: "#98a2b3" },
        formatter: (v) => formatNumber(Math.round(v)),
      },
      max: funnelTotal > 0 ? Math.ceil(funnelTotal * 1.18) : undefined,
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (v) =>
          `${formatNumber(Number(v))} sessions (${formatPercent(Number(v), funnelTotal || 1)} of peak)`,
      },
    },
  };

  const dayLabels = stats.sessionsByDay.map((d) =>
    formatUkDate(d.date, {
      weekday: "short",
      month: "short",
      day: "numeric",
    }),
  );

  const trendOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: "line", zoom: { enabled: false } },
    stroke: { curve: "smooth", width: [3, 3] },
    colors: ["#465fff", "#12b76a"],
    markers: {
      size: 5,
      strokeWidth: 2,
      strokeColors: ["#fff", "#fff"],
      hover: { size: 7 },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1],
      formatter: (val) => (Number(val) > 0 ? formatNumber(Number(val)) : ""),
      style: {
        fontSize: "10px",
        fontWeight: 600,
        colors: ["#101828"],
      },
      background: {
        enabled: true,
        foreColor: "#ffffff",
        borderRadius: 4,
        padding: 3,
        opacity: 0.95,
        borderWidth: 0,
      },
      offsetY: -6,
    },
    xaxis: {
      categories: dayLabels,
      labels: { style: { fontSize: "11px", colors: "#667085" }, rotate: -25 },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", colors: "#98a2b3" },
        formatter: (v) => formatNumber(Math.round(v)),
      },
      min: 0,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { size: 6, shape: "circle" },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      y: {
        formatter: (v) => formatNumber(Number(v)),
      },
    },
  };

  const dropOffOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: "end",
        barHeight: "62%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      formatter: (val) =>
        `${formatNumber(Number(val))}  (${formatPercent(Number(val), dropOffTotal || 1)})`,
      offsetX: 8,
      style: {
        fontSize: "11px",
        fontWeight: 600,
        colors: ["#344054"],
      },
    },
    colors: ["#f79009"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.2,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: stats.stepDropOff.map((s) => s.label),
      labels: {
        style: { fontSize: "11px", colors: "#98a2b3" },
        formatter: (v) => formatNumber(Number(v)),
      },
    },
    yaxis: {
      labels: { style: { fontSize: "12px", colors: "#667085" } },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (v) =>
          `${formatNumber(Number(v))} abandoned (${formatPercent(Number(v), dropOffTotal || 1)})`,
      },
    },
  };

  return (
    <div className="mb-6 space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
        <StatCard
          label="Total sessions"
          value={stats.totalSessions}
          sub={`${formatNumber(stats.totalEvents)} events tracked`}
          accent="brand"
          clickable={Boolean(onApplyFilters)}
          onClick={() => applyStat("all")}
        />
        <StatCard
          label="Completed bookings"
          value={stats.completedSessions}
          sub={`${stats.completionRate}% conversion`}
          accent="success"
          clickable={Boolean(onApplyFilters) && stats.completedSessions > 0}
          onClick={() => applyStat("completed")}
        />
        <StatCard
          label="Abandoned"
          value={stats.abandonedSessions}
          sub={
            stats.avgSessionDurationMinutes != null
              ? `Avg ${stats.avgSessionDurationMinutes} min on form`
              : undefined
          }
          accent="warning"
          clickable={Boolean(onApplyFilters) && stats.abandonedSessions > 0}
          onClick={() => applyStat("abandoned")}
        />
        <StatCard
          label="Cookie consent"
          value={`${stats.cookieConsentRate}%`}
          sub={`${formatNumber(stats.cookieConsentSessions)} of ${formatNumber(stats.totalSessions)} sessions`}
          accent="neutral"
          clickable={Boolean(onApplyFilters) && stats.cookieConsentSessions > 0}
          onClick={() => applyStat("cookieConsent")}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
        <StatCard
          label="Avg events / session"
          value={stats.avgEventsPerSession}
        />
        <StatCard
          label="Payment failures"
          value={stats.paymentFailures}
          accent={stats.paymentFailures > 0 ? "error" : "neutral"}
          clickable={Boolean(onApplyFilters) && stats.paymentFailures > 0}
          onClick={() => applyStat("paymentFailure")}
        />
        <StatCard
          label="Validation errors"
          value={stats.validationFailures}
          accent={stats.validationFailures > 0 ? "warning" : "neutral"}
          clickable={Boolean(onApplyFilters) && stats.validationFailures > 0}
          onClick={() => applyStat("validationFailure")}
        />
        <StatCard
          label="Fare quote failures"
          value={stats.fareQuoteFailures}
          accent={stats.fareQuoteFailures > 0 ? "warning" : "neutral"}
          clickable={Boolean(onApplyFilters) && stats.fareQuoteFailures > 0}
          onClick={() => applyStat("fareQuoteFailure")}
        />
        <StatCard
          label="Call to book clicks"
          value={stats.callClicks ?? 0}
          sub={`${formatNumber(stats.callClickSessions ?? 0)} visitors tapped Call`}
          accent="brand"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
        <StatCard
          label="Avg time on site"
          value={formatEngagementDuration(stats.avgEngagementSeconds)}
          sub="Active time before booking form"
          accent="brand"
        />
        <StatCard
          label="Avg scroll depth"
          value={
            stats.avgScrollDepth != null ? `${stats.avgScrollDepth}%` : "—"
          }
          sub="Max scroll on landing pages"
        />
        <StatCard
          label="Google Ads sessions"
          value={
            stats.trafficSources.find((t) => t.source === "google_ads")?.count ?? 0
          }
          sub="From gclid / paid click params"
          clickable={
            Boolean(onApplyFilters) &&
            (stats.trafficSources.find((t) => t.source === "google_ads")?.count ?? 0) > 0
          }
          onClick={() => applyCustomFilters(applyTrafficSourceFilter("google_ads"))}
        />
        <StatCard
          label="Google Organic"
          value={
            stats.trafficSources.find((t) => t.source === "google_organic")?.count ?? 0
          }
          sub="Referrer google.com (no keyword)"
          clickable={
            Boolean(onApplyFilters) &&
            (stats.trafficSources.find((t) => t.source === "google_organic")?.count ?? 0) > 0
          }
          onClick={() => applyCustomFilters(applyTrafficSourceFilter("google_organic"))}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartPanel
          title="Step funnel"
          subtitle="Exact sessions that reached each booking step"
        >
          {stats.stepFunnel.some((s) => s.count > 0) ? (
            <>
              <Chart
                type="bar"
                height={300}
                options={funnelOptions}
                series={[
                  {
                    name: "Sessions",
                    data: stats.stepFunnel.map((s) => s.count),
                  },
                ]}
              />
              <ExactDataTable
                caption="Step funnel exact values"
                columns={["Step", "Sessions", "% of peak", "Drop vs previous"]}
                onRowClick={
                  onApplyFilters
                    ? (index) =>
                        applyCustomFilters(
                          applyReachedStepFilter(stats.stepFunnel[index].stepName),
                        )
                    : undefined
                }
                rows={stats.stepFunnel.map((step, index) => {
                  const previous =
                    index === 0 ? step.count : stats.stepFunnel[index - 1].count;
                  const drop =
                    index === 0
                      ? "—"
                      : `${formatNumber(Math.max(0, previous - step.count))} (−${formatPercent(
                          Math.max(0, previous - step.count),
                          previous || 1,
                        )})`;
                  return [
                    step.label,
                    step.count,
                    formatPercent(step.count, funnelTotal || 1),
                    drop,
                  ];
                })}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No funnel data yet</p>
          )}
        </ChartPanel>

        <ChartPanel
          title="Sessions over time"
          subtitle="Daily totals — hover points or use the table for exact counts"
        >
          {stats.sessionsByDay.length > 0 ? (
            <>
              <Chart
                type="line"
                height={300}
                options={trendOptions}
                series={[
                  {
                    name: "Sessions",
                    data: stats.sessionsByDay.map((d) => d.sessions),
                  },
                  {
                    name: "Completed",
                    data: stats.sessionsByDay.map((d) => d.completed),
                  },
                ]}
              />
              <ExactDataTable
                caption="Sessions by day exact values"
                columns={["Date", "Sessions", "Completed", "Completion %"]}
                rows={stats.sessionsByDay.map((day) => [
                  formatUkDate(day.date, {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }),
                  day.sessions,
                  day.completed,
                  formatPercent(day.completed, day.sessions || 1),
                ])}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No trend data yet</p>
          )}
        </ChartPanel>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <ChartPanel
          title="Abandonment by last step"
          subtitle="Exact abandoned sessions per last step reached"
        >
          {stats.stepDropOff.length > 0 ? (
            <>
              <Chart
                type="bar"
                height={Math.max(220, stats.stepDropOff.length * 52)}
                options={dropOffOptions}
                series={[
                  {
                    name: "Abandoned",
                    data: stats.stepDropOff.map((s) => s.count),
                  },
                ]}
              />
              <ExactDataTable
                caption="Abandonment exact values"
                columns={["Last step", "Abandoned", "Share"]}
                onRowClick={
                  onApplyFilters
                    ? (index) =>
                        applyCustomFilters(
                          applyAbandonedAtStepFilter(stats.stepDropOff[index].stepName),
                        )
                    : undefined
                }
                rows={stats.stepDropOff.map((step) => [
                  step.label,
                  step.count,
                  formatPercent(step.count, dropOffTotal || 1),
                ])}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No abandoned sessions</p>
          )}
        </ChartPanel>

        <ChartPanel
          title="Devices"
          subtitle={
            consentedSessions > 0
              ? `Based on ${formatNumber(deviceTotal)} consented sessions with device info`
              : "Device data requires cookie consent"
          }
        >
          <BreakdownList
            items={stats.deviceBreakdown}
            labelKey="deviceType"
            total={deviceTotal}
            emptyLabel="No consented device data yet"
            onItemClick={
              onApplyFilters
                ? (item) => applyCustomFilters(applyDeviceFilter(item.deviceType))
                : undefined
            }
          />
          <ExactDataTable
            caption="Device exact values"
            columns={["Device", "Sessions", "Share"]}
            rows={stats.deviceBreakdown.map((item) => [
              item.deviceType,
              item.count,
              formatPercent(item.count, deviceTotal || 1),
            ])}
          />
        </ChartPanel>

        <ChartPanel
          title="Top countries"
          subtitle={
            countryTotal > 0
              ? `Based on ${formatNumber(countryTotal)} sessions with location`
              : "Location data requires consent / geo lookup"
          }
        >
          <BreakdownList
            items={stats.topCountries}
            labelKey="country"
            total={countryTotal}
            emptyLabel="No country data yet"
            onItemClick={
              onApplyFilters
                ? (item) => applyCustomFilters(applyCountryFilter(item.country))
                : undefined
            }
          />
          <ExactDataTable
            caption="Country exact values"
            columns={["Country", "Sessions", "Share"]}
            rows={stats.topCountries.map((item) => [
              item.country,
              item.count,
              formatPercent(item.count, countryTotal || 1),
            ])}
          />
        </ChartPanel>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <ChartPanel
          title="Traffic sources"
          subtitle={
            trafficTotal > 0
              ? `How visitors arrived — ${formatNumber(trafficTotal)} sessions`
              : "Parsed from landing URL + referrer"
          }
        >
          <BreakdownList
            items={stats.trafficSources.map((item) => ({
              ...item,
              displayLabel: item.label || trafficSourceLabel(item.source),
            }))}
            labelKey="displayLabel"
            total={trafficTotal}
            emptyLabel="No attribution data yet"
            onItemClick={
              onApplyFilters
                ? (item) => applyCustomFilters(applyTrafficSourceFilter(item.source))
                : undefined
            }
          />
          <ExactDataTable
            caption="Traffic source exact values"
            columns={["Source", "Sessions", "Share"]}
            rows={stats.trafficSources.map((item) => [
              item.label || trafficSourceLabel(item.source),
              item.count,
              formatPercent(item.count, trafficTotal || 1),
            ])}
          />
        </ChartPanel>

        <ChartPanel
          title="Top landing pages"
          subtitle="First page viewed in the session"
        >
          <BreakdownList
            items={stats.topLandingPages.map((item) => ({
              ...item,
              displayPath: item.path.length > 48 ? `…${item.path.slice(-46)}` : item.path,
            }))}
            labelKey="displayPath"
            total={landingTotal}
            emptyLabel="No landing page data yet"
            onItemClick={
              onApplyFilters
                ? (item) => applyCustomFilters(applyLandingPageFilter(item.path))
                : undefined
            }
          />
          <ExactDataTable
            caption="Landing page exact values"
            columns={["Path", "Sessions", "Share"]}
            rows={stats.topLandingPages.map((item) => [
              item.path,
              item.count,
              formatPercent(item.count, landingTotal || 1),
            ])}
          />
        </ChartPanel>

        <ChartPanel
          title="Paid search terms (UTM)"
          subtitle="utm_term from landing URL — Google Ads only"
        >
          {stats.topUtmTerms.length > 0 ? (
            <>
              <BreakdownList
                items={stats.topUtmTerms}
                labelKey="term"
                total={utmTermTotal}
              />
              <ExactDataTable
                caption="UTM term exact values"
                columns={["Term", "Sessions", "Share"]}
                rows={stats.topUtmTerms.map((item) => [
                  item.term,
                  item.count,
                  formatPercent(item.count, utmTermTotal || 1),
                ])}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">
              No utm_term values yet. Add UTM parameters to ad URLs to see
              keyword-level data here.
            </p>
          )}
        </ChartPanel>
      </div>

      {stats.browserBreakdown.length > 0 && (
        <ChartPanel
          title="Browsers"
          subtitle={`Exact browser counts from ${formatNumber(browserTotal)} consented sessions`}
        >
          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownList
              items={stats.browserBreakdown}
              labelKey="browser"
              total={browserTotal}
              onItemClick={
                onApplyFilters
                  ? (item) => applyCustomFilters(applyBrowserFilter(item.browser))
                  : undefined
              }
            />
            <ExactDataTable
              caption="Browser exact values"
              columns={["Browser", "Sessions", "Share"]}
              rows={stats.browserBreakdown.map((item) => [
                item.browser,
                item.count,
                formatPercent(item.count, browserTotal || 1),
              ])}
            />
          </div>
        </ChartPanel>
      )}
    </div>
  );
}
